import React from 'react';

import { boxStyle } from './styles';

import type { VariantProps } from '@gluestack-ui/nativewind-utils';

import { toDomStyle } from '@/util/domStyle';
import { translateRnA11yProps, type RnAccessibilityProps } from '@/util/accessibilityProps';

type IBoxProps = React.ComponentPropsWithoutRef<'div'> &
  VariantProps<typeof boxStyle> &
  RnAccessibilityProps & {
    className?: string;
    /** Ignored on web; kept for parity with native */
    onLayout?: (...args: any[]) => void;
    /** Maps to data-testid on web for parity with React Native's testID */
    testID?: string;
  };

/**
 * Web‐only Box component
 * ----------------------
 * React DOM doesn’t recognize React-Native-only props, so we either
 * strip them or translate them to ARIA equivalents before spreading
 * onto the `<div>`:
 *   - `onLayout` is dropped (used on native to hide the splash screen).
 *   - All RN accessibility props (accessibilityLabel, nativeID,
 *     accessibilityLiveRegion, accessibilityState, etc.) are
 *     translated to their ARIA / DOM equivalents via
 *     `translateRnA11yProps`. Without this, React warns about
 *     unknown DOM attributes and the attributes end up on the DOM in
 *     invalid lowercase form, so screen readers never see them.
 */
const Box = React.forwardRef<HTMLDivElement, IBoxProps>(
  ({ className, onLayout: _onLayout, testID, style, ...props }, ref) => {
    const { rest, aria } = translateRnA11yProps(props as Record<string, unknown>);

    return (
      <div
        ref={ref}
        className={boxStyle({ class: className })}
        data-testid={testID}
        {...rest}
        {...aria}
      style={toDomStyle(style)}
      />
    );
  }
);

Box.displayName = 'Box';

export { Box };
